import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from '@mui/icons-material/Menu';
import { IconButton } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { MouseEventHandler, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { GuidedTourIcon } from '@/src/shared/assets/GuidedTourIcon';
import { mediumScreen } from '@/src/shared/constants/MediaQueryConstants';
import { chatActions, selectIsChatLoading } from '@/src/store/apps/chat';
import { userSlice } from '@/src/store/apps/user';

import { RecentPopover, useRecentPopover } from './RecentPopover';

const styleMixin = (open?: boolean) => ({
    position: 'relative',
    width: open ? '152px' : '72px',
    border: 'none',
    overflowX: 'hidden',
});

const PermanentDrawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ open }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': styleMixin(open),
}));

const StyledList = styled(List)<{ height: string; isMobile?: boolean }>(({ height, isMobile }) => ({
    height,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isMobile && { padding: '8px' }),
}));

const StyledListItem = ({
    open,
    text,
    icon,
    openedIcon,
    disabled,
    onClick,
    className,
}: {
    open: boolean;
    text: string;
    icon: ReactNode;
    openedIcon?: ReactNode;
    disabled?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
    className?: string;
}) => {
    return (
        <ListItem disablePadding>
            <ListItemButton
                disabled={disabled}
                onClick={onClick}
                sx={[
                    {
                        minHeight: 48,
                        px: 2.5,
                    },
                    open
                        ? {
                              justifyContent: 'initial',
                          }
                        : {
                              justifyContent: 'center',
                          },
                ]}
            >
                <ListItemIcon
                    className={className}
                    sx={[
                        {
                            minWidth: 0,
                            justifyContent: 'center',
                        },
                        open
                            ? {
                                  mr: 3,
                              }
                            : null,
                    ]}
                >
                    {open && openedIcon ? openedIcon : icon}
                </ListItemIcon>
                {open && <ListItemText primary={text} />}
            </ListItemButton>
        </ListItem>
    );
};

export const AppDrawer = () => {
    const { t } = useTranslation(['common', 'ginger']);
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const isChatLoading = useSelector(selectIsChatLoading);
    const isMobile: boolean = useMediaQuery(mediumScreen);

    const toggleDrawer = useCallback(() => {
        setOpen((previousValue) => !previousValue);
    }, []);

    const closeDrawer = useCallback(() => setOpen(false), []);

    const addConversation = useCallback(() => {
        dispatch(chatActions.switchToNewConversation());
        if (isMobile) closeDrawer();
    }, [dispatch, isMobile]);

    const { recentPopoverAnchorEl, openRecentPopover, closeRecentPopover } = useRecentPopover();

    const guidedTourIconClick = () => {
        if (isMobile) closeDrawer();
        dispatch(userSlice.actions.runGuidedTour(true));
    };

    const drawerContent = (
        <>
            <StyledList height="72px" isMobile={isMobile}>
                <StyledListItem
                    open={open}
                    text={t('action.collapse')}
                    icon={isMobile ? <CloseIcon /> : <MenuIcon />}
                    openedIcon={!isMobile && <CloseIcon />}
                    onClick={() => {
                        if (isMobile) {
                            closeDrawer();
                        } else {
                            toggleDrawer();
                        }
                    }}
                />
            </StyledList>
            <StyledList height="100%">
                <StyledListItem
                    open={open}
                    className="new-chat"
                    text={t('web_channel.conversation.new_chat')}
                    icon={<AddIcon />}
                    disabled={isChatLoading}
                    onClick={addConversation}
                />
                <StyledListItem
                    open={open}
                    className="recent-chat"
                    text={t('web_channel.conversation.recent')}
                    icon={<HistoryIcon />}
                    disabled={isChatLoading}
                    onClick={openRecentPopover}
                />
            </StyledList>
            <StyledList height="72px">
                <StyledListItem
                    open={open}
                    text={t('guided_tour.dialog.title', { ns: 'ginger' })}
                    icon={<GuidedTourIcon />}
                    onClick={guidedTourIconClick}
                />
            </StyledList>
            <RecentPopover
                open={!!recentPopoverAnchorEl}
                anchorEl={recentPopoverAnchorEl}
                onDrawerClose={closeDrawer}
                onClose={closeRecentPopover}
            />
        </>
    );

    return (
        <>
            {isMobile && !open && (
                <IconButton
                    className="drawer-menu-icon"
                    onClick={toggleDrawer}
                    sx={{ position: 'fixed', top: 16, left: 16, zIndex: 1300 }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {isMobile ? (
                <MuiDrawer
                    anchor="left"
                    variant="temporary"
                    open={open}
                    onClose={closeDrawer}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            borderRadius: 0,
                        },
                    }}
                >
                    {drawerContent}
                </MuiDrawer>
            ) : (
                <PermanentDrawer variant="permanent" open={open}>
                    {drawerContent}
                </PermanentDrawer>
            )}
        </>
    );
};
